import React from "react";
import { Box, type SpaceProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { FillImage } from "components/images";
import { TextTitle, type TextTitleProps } from "ui/text";
import { Button } from "ui/button";
import { MediaAccordion } from "ui/accordion";
import composableCdpImage from "../../../public/assets/shared-illustrations/composable-cdp.png";

export type ComposableCdpAccordionProps = {
  sectionTitle?: string;
  sectionDescription?: string;
  sectionMaxW?: TextTitleProps["maxW"];
  theme?: Theme;
};

export function ComposableCdpAccordion({
  sectionTitle,
  sectionDescription,
  sectionMaxW,
  theme = "light",
  ...spaceProps
}: ComposableCdpAccordionProps & SpaceProps) {
  return (
    <Box {...spaceProps}>
      {sectionTitle && (
        <TextTitle
          align="center"
          size="lg"
          theme={theme}
          mb={{ base: "biggest", md: "huge" }}
          title={sectionTitle}
          maxW={sectionMaxW}
          description={sectionDescription}
          actions={
            <Button hasRightChevron href="/platform/composable-cdp">
              The Composable CDP
            </Button>
          }
        />
      )}

      <MediaAccordion
        variant="static"
        theme={theme}
        media={
          <FillImage
            src={composableCdpImage}
            alt="Flow between the the data warehouse, Hightouch as a Composable CDP, and user's integration ecosystem."
          />
        }
        items={[
          {
            title: "Leverage your existing data",
            content:
              "Hightouch is built around your data warehouse. It brings a full set of CDP capabilities to your existing data investment without requiring any change to your tech stack, copying of data, or introducing security risks.",
          },
          {
            title: "Use all of your data, not just “CDP data”",
            content:
              "Traditional CDPs are limited to user and event data. With Hightouch, you can leverage all of your data, from any source, with infinite look back and the flexibility to create complex relationships.",
          },
          {
            title: "Keep control of your data",
            content:
              "Hightouch doesn’t store your data and offers advanced governance features, version control, support for multi-region cloud deployments, and more.",
          },
          {
            title: "Free your data team for high-value work",
            content:
              "Secure, self-service access to marketing data keeps data engineering and data science work as the priority - not servicing endless tickets and A/B test readouts.",
          },
        ]}
      />
    </Box>
  );
}
